import { AppDispatch } from 'common/store/store';
import { resetUploadData, seedDefaultReminderLeadTime } from 'common/store/appSlice';
import { linkPath } from 'common/constants';
import { MixpanelEventName, trackEvent } from 'common/utils/mixpanel/eventTriggers';
import { setUploadEntryPath } from './uploadEntryPath';
import { applyUploadPreset, getUploadPreset, UploadPreset } from './uploadPreset';

// Loosely typed to match the ad-hoc shape every other call site reads off
// `state.auth.user` (AddDoc, UploadDocument/index.tsx, etc.) - some responses
// nest the real profile under `.user`, some don't, and there's no single
// exported type that reliably covers both.
type UploadFlowUser = {
  user?: { default_reminder_before?: number | null; default_reminder_before_type?: string | null } | null;
  default_reminder_before?: number | null;
  default_reminder_before_type?: string | null;
} | null | undefined;

export interface StartUploadFlowParams {
  dispatch: AppDispatch;
  // Called with the resolved target path once setup completes (paywall
  // permitting). AddDoc's "+" button passes its own `navigate(path)`
  // directly; the shortcut deep-link handler passes `queueNavigation` so an
  // unauthenticated cold start goes through the existing auth-gated queue
  // instead of a direct route push.
  navigate: (path: string) => void;
  hasSubscribed: boolean;
  // Matches usePaywall()'s showPaywall - fire-and-forget here, exactly as
  // the original "+" button call site treated it (not awaited).
  showPaywall: (options?: { useFullscreen?: boolean; source?: string }) => unknown;
  user: UploadFlowUser;
  entryPathname: string;
  entrySearch?: string;
  targetPath?: string;
  // Forwarded to setUploadEntryPath - see its own doc comment. Defaults to
  // true (every pre-existing caller records its history index); the "Scan a
  // document" shortcut is the one caller that must pass false.
  recordHistoryIdx?: boolean;
  // Which entry point started the flow, for analytics only.
  source?: string;
  // Category/group to pre-fill after the reset. Omitted (undefined) means
  // "whatever the page on screen registered" (see uploadPreset.ts) - that is
  // how the bottom-nav "+" on a category or group page keeps its context.
  // Pass null to force an empty draft.
  preset?: UploadPreset | null;
}

/**
 * The exact setup the dashboard "+" button (AddDoc.tsx) performs before
 * entering the upload flow, extracted so the home-screen "Scan a document"
 * shortcut can reuse it byte-for-byte instead of drifting from it:
 *
 * 1. Paywall gate - blocks (and returns false) without touching anything
 *    else if the user isn't subscribed.
 * 2. Reset the upload draft.
 * 3. Seed the draft's reminder lead time from the user's saved default.
 *    Then re-apply the category/group preset, if the user is starting
 *    from inside a category or group page.
 * 4. Record where the user entered from, so Discard can return them there.
 * 5. Navigate to the target screen (upload, by default).
 *
 * Returns whether the flow actually proceeded (false means the paywall
 * blocked it and nothing else ran).
 */
export const startUploadFlow = async ({
  dispatch,
  navigate,
  hasSubscribed,
  showPaywall,
  user,
  entryPathname,
  entrySearch = '',
  targetPath = linkPath.documentUpload,
  recordHistoryIdx,
  source = 'unknown',
  preset,
}: StartUploadFlowParams): Promise<boolean> => {
  // Check subscription before allowing document upload
  if (!hasSubscribed) {
    showPaywall({ source });
    return false;
  }
  trackEvent(MixpanelEventName.uploadStarted, { source });
  await dispatch(resetUploadData());
  // Seed the fresh draft with the user's saved default reminder lead time
  // (Notifications settings). Null/unset profile values leave the
  // reducer's own static default in place.
  const defaultBefore = user?.user?.default_reminder_before ?? user?.default_reminder_before ?? null;
  const defaultBeforeType = user?.user?.default_reminder_before_type ?? user?.default_reminder_before_type ?? null;
  if (defaultBefore != null && defaultBeforeType) {
    dispatch(seedDefaultReminderLeadTime({ before: defaultBefore, beforeType: defaultBeforeType }));
  }
  const activePreset = preset === undefined ? getUploadPreset() : preset;
  applyUploadPreset(dispatch, activePreset);
  const label = activePreset
    ? (activePreset.group?.name || activePreset.vehicle?.name || activePreset.property?.name
      || activePreset.category.name)
    : undefined;
  setUploadEntryPath(entryPathname, entrySearch, { recordHistoryIdx, label });
  navigate(targetPath);
  return true;
};
